import { useEffect, useRef, useState } from 'react';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DOLE_COORDS, type GeoPoint } from '@/lib/geo';

interface LocationPickerProps {
    point: GeoPoint | null;
    onChange: (point: GeoPoint | null) => void;
}

const MARKER_ICON = L.divIcon({
    className: '',
    html: '<div class="marker-pin"></div>',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

export default function LocationPicker({ point, onChange }: LocationPickerProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);

    const [locating, setLocating] = useState(false);
    const [latText, setLatText] = useState(point ? String(point.latitude) : '');
    const [lngText, setLngText] = useState(point ? String(point.longitude) : '');
    const [notice, setNotice] = useState<string | null>(null);

    const center = point ?? DOLE_COORDS;

    useEffect(() => {
        if (!containerRef.current) return;

        const map = L.map(containerRef.current, {
            center: [center.latitude, center.longitude],
            zoom: 15,
            scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="nofollow noopener">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(map);

        const marker = L.marker([center.latitude, center.longitude], {
            draggable: true,
            icon: MARKER_ICON,
        }).addTo(map);

        marker.on('dragend', () => {
            const p = marker.getLatLng();
            setLatText(p.lat.toFixed(5));
            setLngText(p.lng.toFixed(5));
            setNotice(null);
            onChange({ latitude: p.lat, longitude: p.lng });
        });

        mapRef.current = map;
        markerRef.current = marker;

        return () => {
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const moveMarker = (lat: number, lng: number) => {
        markerRef.current?.setLatLng([lat, lng]);
        if (mapRef.current) mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 15));
    };


    const applyManual = () => {
        if (!latText.trim() && !lngText.trim()) {
            setNotice(null);
            return;
        }

        const lat = parseFloat(latText);
        const lng = parseFloat(lngText);

        if (
            Number.isNaN(lat) ||
            Number.isNaN(lng) ||
            lat < -90 ||
            lat > 90 ||
            lng < -180 ||
            lng > 180
        ) {
            setNotice('Coordonnées invalides');
            return;
        }

        setNotice(null);
        moveMarker(lat, lng);
        onChange({ latitude: lat, longitude: lng });
    };

    const handleClear = () => {
        onChange(null);
        setLatText('');
        setLngText('');
        setNotice(null);
        moveMarker(DOLE_COORDS.latitude, DOLE_COORDS.longitude);
    };

    return (
        <div className="rounded-md border border-background-200 overflow-hidden">
            <div ref={containerRef} className="relative w-full h-[260px] bg-background-100" />

            <div className="p-3 flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                    {point && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="px-2 py-1 text-xs font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
                        >
                            Effacer
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <label htmlFor="activity-lat" className="text-xs font-medium text-foreground-500 whitespace-nowrap">
                        Latitude
                    </label>

                    <input
                        id="activity-lat"
                        name="latitude"
                        type="number"
                        step="0.0001"
                        value={latText}
                        onChange={(e) => setLatText(e.target.value)}
                        onBlur={applyManual}
                        placeholder={String(DOLE_COORDS.latitude)}
                        className="w-full min-w-0 px-2 py-1.5 rounded-md border border-background-200 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />

                    <label htmlFor="activity-lng" className="text-xs font-medium text-foreground-500 whitespace-nowrap">
                        Longitude
                    </label>

                    <input
                        id="activity-lng"
                        name="longitude"
                        type="number"
                        step="0.0001"
                        value={lngText}
                        onChange={(e) => setLngText(e.target.value)}
                        onBlur={applyManual}
                        placeholder={String(DOLE_COORDS.longitude)}
                        className="w-full min-w-0 px-2 py-1.5 rounded-md border border-background-200 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                </div>

                <p className="text-xs text-foreground-400">
                    Glisse le marqueur sur la carte pour affiner la position, ou saisis les coordonnées.
                </p>

                {notice && <span className="text-xs text-primary-600">{notice}</span>}
            </div>
        </div>
    );
}